(() => {
  const clamp = value => Math.max(0, Math.min(1, value));
  const motion = matchMedia('(prefers-reduced-motion: reduce)');
  const initialised = new WeakSet();
  const cleanups = new WeakMap();

  function initialise(scope = document) {
    scope.querySelectorAll('[data-ms-entrance]').forEach(dialog => {
      if (initialised.has(dialog)) return;
      initialised.add(dialog);
      let entered = false;
      try { entered = localStorage.getItem('ms-entered') === '1'; } catch {}
      if (window.Shopify?.designMode || entered) return;
      let leaving = false;
      let timer;
      const remember = () => { try { localStorage.setItem('ms-entered', '1'); } catch {} };
      const leave = () => {
        if (leaving) return;
        leaving = true;
        remember();
        dialog.classList.add('is-entering');
        timer = setTimeout(() => window.MSDialogs?.close(dialog) || (dialog.open && dialog.close()), motion.matches ? 0 : 650);
      };
      dialog.querySelectorAll('[data-ms-enter]').forEach(button => button.addEventListener('click', leave));
      dialog.querySelector('[data-ms-enter-shop]')?.addEventListener('click', remember);
      dialog.addEventListener('cancel', event => { event.preventDefault(); leave(); });
      dialog.addEventListener('close', remember);
      dialog.querySelector('[data-ms-world-sound]')?.addEventListener('click', () => window.MSSound?.toggle());
      const soundChanged = () => {
        const enabled = !!window.MSSound?.enabled;
        const button = dialog.querySelector('[data-ms-world-sound]');
        const label = dialog.querySelector('[data-ms-sound-label]');
        button?.setAttribute('aria-pressed', String(enabled));
        if (label) label.textContent = enabled ? label.dataset.on : label.dataset.off;
        const soundOn = button?.querySelector('[data-world-sound-on]');
        const soundOff = button?.querySelector('[data-world-sound-off]');
        if (soundOn) soundOn.hidden = !enabled;
        if (soundOff) soundOff.hidden = enabled;
      };
      document.addEventListener('ms:soundchange', soundChanged);
      soundChanged();
      if (window.MSDialogs) window.MSDialogs.open(dialog); else dialog.showModal();
      cleanups.set(dialog, () => { clearTimeout(timer); document.removeEventListener('ms:soundchange', soundChanged); window.MSDialogs?.close(dialog); });
    });

    scope.querySelectorAll('[data-ms-scroll-scene]').forEach(scene => {
      if (initialised.has(scene)) return;
      initialised.add(scene);
      const video = scene.querySelector('[data-ms-scene-video]');
      const layer = scene.querySelector('[data-ms-scene-layer]');
      const band = scene.querySelector('[data-ms-scene-band]');
      if (!video || !layer || !band) return;
      const small = matchMedia('(max-width:860px)');
      const coarse = matchMedia('(hover:none) and (pointer:coarse)').matches;
      let destroyed = false;
      let frame = 0;
      let objectUrl;
      let controller;
      let source;
      let ready = false;
      let current = 0;
      let target = 0;
      let dirty = true;
      let userReady = false;
      let top = 0;
      let total = 1;
      let viewportWidth = innerWidth;
      const mobile = () => coarse || small.matches;
      const read = () => {
        const x = clamp((scrollY - top) / total);
        target = 0.85 * x + 0.15 * (4 * (x - 0.5) ** 3 + 0.5);
        scene.style.setProperty('--ss-progress', String(x));
      };
      const tick = () => {
        frame = 0;
        if (destroyed || motion.matches) return;
        if (dirty) { dirty = false; read(); }
        if (ready && !video.seeking && Number.isFinite(video.duration)) {
          current += (target - current) * 0.2;
          const time = Math.min(0.999, current) * video.duration;
          if (Math.abs(video.currentTime - time) > (mobile() ? 0.02 : 0.008)) {
            try { video.currentTime = time; } catch {}
          }
        }
        if (dirty || (ready && (Math.abs(current - target) > 0.001 || video.seeking))) frame = requestAnimationFrame(tick);
      };
      const schedule = () => { if (!frame && !destroyed && !motion.matches) frame = requestAnimationFrame(tick); };
      const unload = () => {
        controller?.abort();
        video.pause();
        video.removeAttribute('src');
        video.load();
        if (objectUrl) URL.revokeObjectURL(objectUrl);
        objectUrl = undefined;
        ready = false;
        delete layer.dataset.videoPainted;
      };
      const load = async () => {
        const next = mobile() ? video.dataset.mobile : video.dataset.desktop;
        if (motion.matches || !next || source === next) return;
        unload();
        source = next;
        controller = new AbortController();
        const request = controller;
        try {
          const response = await fetch(next, { signal: request.signal });
          if (!response.ok) throw new Error('Film unavailable');
          const blob = await response.blob();
          if (destroyed || request.signal.aborted) return;
          objectUrl = URL.createObjectURL(blob);
          video.src = objectUrl;
          video.load();
        } catch (error) {
          if (error.name !== 'AbortError') { unload(); source = next; }
        }
      };
      const layout = () => {
        top = scene.getBoundingClientRect().top + scrollY;
        total = Math.max(1, band.getBoundingClientRect().height);
        viewportWidth = innerWidth;
        dirty = true;
        void load();
        schedule();
      };
      const scroll = () => { dirty = true; schedule(); };
      const resize = () => { if (!coarse || innerWidth !== viewportWidth) layout(); };
      const metadata = () => { ready = true; dirty = true; schedule(); };
      const painted = () => { if (ready) layer.dataset.videoPainted = 'true'; schedule(); };
      const fail = () => { unload(); };
      const reduce = () => {
        if (motion.matches) { cancelAnimationFrame(frame); frame = 0; unload(); source = undefined; }
        else layout();
      };
      const prime = () => {
        userReady = true;
        if (!mobile() || !ready || motion.matches) return;
        void video.play().then(() => { video.pause(); schedule(); }).catch(() => {});
      };
      video.addEventListener('loadedmetadata', metadata);
      video.addEventListener('loadeddata', () => { if (userReady) prime(); });
      video.addEventListener('seeked', painted);
      video.addEventListener('error', fail);
      addEventListener('scroll', scroll, { passive:true });
      addEventListener('resize', resize);
      addEventListener('orientationchange', layout);
      addEventListener('pointerdown', prime, { once:true, passive:true });
      motion.addEventListener('change', reduce);
      layout();
      cleanups.set(scene, () => {
        destroyed = true;
        cancelAnimationFrame(frame);
        unload();
        removeEventListener('scroll', scroll);
        removeEventListener('resize', resize);
        removeEventListener('orientationchange', layout);
        removeEventListener('pointerdown', prime);
        motion.removeEventListener('change', reduce);
      });
    });

    scope.querySelectorAll('[data-ms-spatial]').forEach(frame => {
      if (initialised.has(frame)) return;
      initialised.add(frame);
      const photo = frame.querySelector('.spatial-photo');
      frame.addEventListener('pointermove', event => {
        if (motion.matches || event.pointerType !== 'mouse' || !photo) return;
        const r = frame.getBoundingClientRect();
        photo.style.setProperty('--room-x', ((event.clientX-r.left)/r.width-.5)*-10+'px');
        photo.style.setProperty('--room-y', ((event.clientY-r.top)/r.height-.5)*-6+'px');
      });
      frame.addEventListener('pointerleave', () => { photo?.style.setProperty('--room-x','0px'); photo?.style.setProperty('--room-y','0px'); });
    });

    scope.querySelectorAll('[data-ms-nocturnal]').forEach(section => {
      if (initialised.has(section)) return;
      initialised.add(section);
      let frame = 0;
      let taps = 0;
      const update = () => {
        cancelAnimationFrame(frame);
        if (motion.matches) return;
        frame = requestAnimationFrame(() => {
          const r = section.getBoundingClientRect();
          section.style.setProperty('--night', String(clamp((-r.top + innerHeight * .15) / (r.height * .85))));
        });
      };
      section.querySelector('[data-ms-notice]')?.addEventListener('click', () => {
        taps++;
        const noticed = section.querySelector('[data-ms-noticed]');
        if (taps >= 3 && noticed) noticed.textContent = noticed.dataset.message;
      });
      addEventListener('scroll', update, { passive:true });
      update();
      cleanups.set(section, () => { cancelAnimationFrame(frame); removeEventListener('scroll', update); });
    });
  }

  initialise();
  document.addEventListener('shopify:section:load', event => initialise(event.target));
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll('[data-ms-entrance],[data-ms-scroll-scene],[data-ms-nocturnal]').forEach(node => cleanups.get(node)?.());
  });
})();
